import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import BackButton from '../components/BackButton';

import { API_URL as API } from '../config/api';
const SPORTS = ['Football', 'Cricket', 'Tennis'];

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
}

export default function Checkout() {
  const { state } = useLocation();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { id: turfIdFromUrl } = useParams();
  const isMobile = useWindowWidth() < 768;
  const [turf, setTurf] = useState(state?.turf || null);
  const [turfLoading, setTurfLoading] = useState(!state?.turf);
  const [turfError, setTurfError] = useState(false);
  const [date, setDate] = useState(state?.date || new Date().toISOString().split('T')[0]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [sport, setSport] = useState(state?.turf?.sport || 'Football');
  const [requestSent, setRequestSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [showFeeBreakdown, setShowFeeBreakdown] = useState(false);

  // If turf wasn't passed via state (e.g. page refresh), turfIdFromUrl comes from useParams above
  useEffect(() => {
    if (!turf && turfIdFromUrl) {
      setTurfLoading(true);
      setTurfError(false);
      axios.get(`${API}/turfs/${turfIdFromUrl}`)
        .then(res => { setTurf(res.data); setSport(res.data.sport || 'Football'); })
        .catch(() => setTurfError(true))
        .finally(() => setTurfLoading(false));
    } else {
      setTurfLoading(false);
    }
  }, [turfIdFromUrl]);

  const turfId = turf?._id || turf?.id;

  // Helper to parse 12h or 24h string into minutes from midnight
  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)?/i);
    if (!match) {
      const hMatch = timeStr.match(/(\d+)\s*(AM|PM)?/i);
      if (!hMatch) return 0;
      let h = parseInt(hMatch[1], 10);
      const period = hMatch[2]?.toUpperCase();
      if (period === 'PM' && h !== 12) h += 12;
      if (period === 'AM' && h === 12) h = 0;
      return h * 60;
    }
    let h = parseInt(match[1], 10);
    const m = parseInt(match[2], 10);
    const period = match[3]?.toUpperCase();
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h * 60 + m;
  };

  // Helper to check if a day matches a rule's dayType / range
  const doesDayMatchRule = (ruleType, dayOfWeek) => {
    // dayOfWeek: 0 = Sun, 1 = Mon, ..., 6 = Sat
    if (!ruleType || ruleType === 'All Days') return true;
    
    // Normalize ruleType string: strip parenthetical notes like "(Weekdays)", "(Weekends)"
    const cleanType = String(ruleType).replace(/\s*\([^)]*\)/g, '').trim();
    const lower = cleanType.toLowerCase();

    if (lower === 'weekday' || lower === 'weekdays' || lower === 'monday - friday' || lower === 'mon - fri') {
      return dayOfWeek >= 1 && dayOfWeek <= 5;
    }
    if (lower === 'weekend' || lower === 'weekends' || lower === 'saturday - sunday' || lower === 'sat - sun') {
      return dayOfWeek === 0 || dayOfWeek === 6;
    }
    if (lower === 'monday - thursday' || lower === 'mon - thu') return dayOfWeek >= 1 && dayOfWeek <= 4;
    if (lower === 'friday - sunday' || lower === 'fri - sun') return dayOfWeek === 5 || dayOfWeek === 6 || dayOfWeek === 0;
    if (lower === 'monday - wednesday' || lower === 'mon - wed') return dayOfWeek >= 1 && dayOfWeek <= 3;
    if (lower === 'thursday - sunday' || lower === 'thu - sun') return dayOfWeek >= 4 || dayOfWeek === 0;

    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const currentDayName = dayNames[dayOfWeek];
    if (lower === currentDayName.toLowerCase()) return true;

    if (cleanType.includes('-')) {
      const parts = cleanType.split('-').map(s => s.trim().toLowerCase());
      if (parts.length === 2) {
        const startIdx = dayNames.findIndex(d => d.toLowerCase().startsWith(parts[0].slice(0, 3)));
        const endIdx = dayNames.findIndex(d => d.toLowerCase().startsWith(parts[1].slice(0, 3)));
        if (startIdx !== -1 && endIdx !== -1) {
          if (startIdx <= endIdx) {
            return dayOfWeek >= startIdx && dayOfWeek <= endIdx;
          } else {
            return dayOfWeek >= startIdx || dayOfWeek <= endIdx;
          }
        }
      }
    }

    return false;
  };

  // ── PEAK HOUR PRICING ──
  // Returns the correct price for a slot based on turf pricing and rules
  const getPriceForSlot = (slotHour, slot) => {
    // 1. If slot object has a direct custom price set by partner, prioritize it
    if (slot && slot.price !== undefined && slot.price !== null && slot.price !== '') {
      const numPrice = Number(slot.price);
      if (!isNaN(numPrice) && numPrice > 0) {
        return numPrice;
      }
    }

    const rules = turf?.pricingRules || [];
    const bookingDate = new Date(date + 'T00:00:00');
    const dayOfWeek = isNaN(bookingDate.getDay()) ? new Date().getDay() : bookingDate.getDay();
    const slotMinutes = slotHour * 60;

    // 2. Check explicit pricing rules array
    if (rules && rules.length > 0) {
      const matchingRules = rules.filter(r => {
        if (!r.active || r.type === 'slots_config' || r.id === 'turf_slots_config') return false;
        if (!doesDayMatchRule(r.type, dayOfWeek)) return false;

        const ruleStart = parseTimeToMinutes(r.startTime);
        const ruleEnd = parseTimeToMinutes(r.endTime);

        if (ruleStart < ruleEnd) {
          return slotMinutes >= ruleStart && slotMinutes < ruleEnd;
        } else if (ruleStart > ruleEnd) {
          // spans midnight
          return slotMinutes >= ruleStart || slotMinutes < ruleEnd;
        }
        return false;
      });

      if (matchingRules.length > 0) {
        matchingRules.sort((a, b) => {
          const isSpecificA = a.type !== 'All Days' ? 2 : 1;
          const isSpecificB = b.type !== 'All Days' ? 2 : 1;
          return isSpecificB - isSpecificA;
        });
        return matchingRules[0].price;
      }
    }

    // 3. Check slots_config.timings or turf.timings (Day Schedules configured by partner)
    let slotsConfig = rules.find(r => r.type === 'slots_config' || r.id === 'turf_slots_config');
    if (!slotsConfig && turf?._id) {
      try {
        const localRules = JSON.parse(localStorage.getItem(`turf_pricing_rules_${turf._id}`) || '[]');
        slotsConfig = localRules.find(r => r.type === 'slots_config' || r.id === 'turf_slots_config');
      } catch (e) {}
    }

    const timingsList = slotsConfig?.timings || turf?.timings || [];
    if (Array.isArray(timingsList) && timingsList.length > 0) {
      const matchingTiming = timingsList.find(t => {
        const tPrice = parseFloat(t.price);
        if (!tPrice || tPrice <= 0) return false;
        const tName = t.name === 'Custom Days Range...' && t.customName ? t.customName : t.name;
        if (!doesDayMatchRule(tName, dayOfWeek)) return false;

        const tStart = parseTimeToMinutes(t.open || '06:00 AM');
        const tEnd = parseTimeToMinutes(t.close || '10:00 PM');

        if (tStart < tEnd) {
          return slotMinutes >= tStart && slotMinutes < tEnd;
        } else if (tStart > tEnd) {
          return slotMinutes >= tStart || slotMinutes < tEnd;
        }
        return true;
      });

      if (matchingTiming && matchingTiming.price) {
        return parseFloat(matchingTiming.price);
      }
    }

    // 4. Fallback to base price or first timing price
    const fallbackTimingPrice = timingsList.find(t => t.price && parseFloat(t.price) > 0)?.price;
    return parseFloat(turf?.price_per_hour) || parseFloat(fallbackTimingPrice) || 0;
  };

  // Helper to parse start hour from slot label
  const parseStartHour = (slotStr) => {
    if (!slotStr) return 6;
    const match = slotStr.match(/(\d+)(?::(\d+))?\s*(AM|PM)?/i);
    if (!match) return 6;
    let h = parseInt(match[1], 10);
    const period = match[3] ? match[3].toUpperCase() : null;
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h;
  };

  // Helper to generate 1-hour slots from open and close time
  const generateSlotsFromTimeRange = (openStr, closeStr, defaultPrice) => {
    const startMin = parseTimeToMinutes(openStr || '06:00 AM');
    let endMin = parseTimeToMinutes(closeStr || '10:00 PM');
    if (endMin <= startMin) endMin += 24 * 60;

    const slots = [];
    for (let m = startMin; m < endMin; m += 60) {
      const nextM = m + 60;
      const fmt = (mins) => {
        let normalized = mins % (24 * 60);
        let h = Math.floor(normalized / 60);
        let min = normalized % 60;
        let period = h >= 12 ? 'PM' : 'AM';
        let h12 = h % 12;
        if (h12 === 0) h12 = 12;
        return `${String(h12).padStart(2, '0')}:${String(min).padStart(2, '0')} ${period}`;
      };
      const label = `${fmt(m)} - ${fmt(nextM)}`;
      slots.push({
        label,
        value: label,
        hour: Math.floor((m % (24 * 60)) / 60),
        price: defaultPrice
      });
    }
    return slots;
  };

  // Generate hourly slots (6 AM to 10 PM default)
  const generateHourlySlots = () => {
    const defaultSlots = [
      "06:00 AM - 07:00 AM", "07:00 AM - 08:00 AM", "08:00 AM - 09:00 AM", "09:00 AM - 10:00 AM",
      "10:00 AM - 11:00 AM", "11:00 AM - 12:00 PM", "12:00 PM - 01:00 PM", "01:00 PM - 02:00 PM",
      "02:00 PM - 03:00 PM", "03:00 PM - 04:00 PM", "04:00 PM - 05:00 PM", "05:00 PM - 06:00 PM",
      "06:00 PM - 07:00 PM", "07:00 PM - 08:00 PM", "08:00 PM - 09:00 PM", "09:00 PM - 10:00 PM"
    ];
    return defaultSlots.map(slotLabel => ({
      label: slotLabel,
      value: slotLabel,
      hour: parseStartHour(slotLabel)
    }));
  };

  // Get available slots configured by partner for weekday vs weekend
  const getAvailableSlotsForDate = () => {
    if (!date) return generateHourlySlots();

    const dateObj = new Date(date + 'T00:00:00');
    const dayOfWeek = isNaN(dateObj.getDay()) ? new Date().getDay() : dateObj.getDay();
    const isWeekend = (dayOfWeek === 0 || dayOfWeek === 6);

    const rules = turf?.pricingRules || [];
    let slotsConfig = rules.find(r => r.type === 'slots_config' || r.id === 'turf_slots_config');

    if (!slotsConfig && turf?._id) {
      try {
        const localRules = JSON.parse(localStorage.getItem(`turf_pricing_rules_${turf._id}`) || '[]');
        slotsConfig = localRules.find(r => r.type === 'slots_config' || r.id === 'turf_slots_config');
      } catch (e) {}
    }

    const timingsList = slotsConfig?.timings || turf?.timings || [];
    const matchingTimings = timingsList.filter(t => {
      const tName = t.name === 'Custom Days Range...' && t.customName ? t.customName : t.name;
      return doesDayMatchRule(tName, dayOfWeek);
    });

    if (matchingTimings.length > 0) {
      let combined = [];
      matchingTimings.forEach(t => {
        const tPrice = parseFloat(t.price || turf?.price_per_hour) || 0;
        if (Array.isArray(t.slots) && t.slots.length > 0) {
          const active = t.slots.filter(s => (typeof s === 'string') || (s && s.isActive !== false));
          active.forEach(s => {
            const slotLabel = typeof s === 'string' ? s : (s?.label || s?.slot || '');
            const slotPrice = (typeof s === 'object' && s?.price !== undefined && s?.price !== null && s?.price !== '')
              ? parseFloat(s.price)
              : tPrice;
            if (slotLabel && !combined.some(existing => existing.label === slotLabel)) {
              combined.push({
                label: slotLabel,
                value: slotLabel,
                hour: parseStartHour(slotLabel),
                price: slotPrice
              });
            }
          });
        } else if (t.open && t.close) {
          const gen = generateSlotsFromTimeRange(t.open, t.close, tPrice);
          gen.forEach(s => {
            if (!combined.some(existing => existing.label === s.label)) {
              combined.push(s);
            }
          });
        }
      });

      if (combined.length > 0) {
        combined.sort((a, b) => a.hour - b.hour);
        return combined;
      }
    }

    if (slotsConfig) {
      const rawList = isWeekend ? (slotsConfig.weekendSlots || []) : (slotsConfig.weekdaySlots || []);
      const activeList = rawList.filter(s => (typeof s === 'string') || (s && s.isActive !== false));
      return activeList.map(s => {
        const slotLabel = typeof s === 'string' ? s : (s?.label || s?.slot || '');
        const slotPrice = typeof s === 'object' && s?.price !== undefined ? s.price : undefined;
        return {
          label: slotLabel,
          value: slotLabel,
          hour: parseStartHour(slotLabel),
          price: slotPrice
        };
      });
    }

    return generateHourlySlots();
  };

  const hourlySlots = getAvailableSlotsForDate();

  // ── DYNAMIC FEE STATE ─────────────────────────────────────────────────────
  const [feeBreakdown, setFeeBreakdown] = useState(null);

  // Base court price from selected slots — rounded to whole number
  const slotPrices = selectedSlots.map(label => {
    const slot = hourlySlots.find(s => s.label === label);
    return slot ? Math.round(getPriceForSlot(slot.hour, slot)) : Math.round(turf?.price_per_hour || 0);
  });
  const courtPrice = slotPrices.reduce((sum, p) => sum + p, 0);
  const numberOfSlots = selectedSlots.length;

  // Extract applied offer from location state
  const appliedOffer = state?.appliedOffer || null;
  const parseDiscountPercent = (discountStr) => {
    if (!discountStr) return 0;
    const match = discountStr.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  };
  const discountPercent = appliedOffer ? parseDiscountPercent(appliedOffer.discount) : 0;
  const discountAmount = Math.round(courtPrice * (discountPercent / 100));
  const discountedBase = Math.max(0, courtPrice - discountAmount);

  // Fetch fee breakdown from backend whenever base price changes
  useEffect(() => {
    if (discountedBase <= 0) {
      setFeeBreakdown(null);
      return;
    }
    axios.get(`${API}/bookings/fee-preview?base_amount=${discountedBase}`)
      .then(res => setFeeBreakdown(res.data))
      .catch(() => {
        // Fallback: calculate locally with 5% defaults — all rounded to integers
        const base    = Math.round(discountedBase);
        const userFee = Math.round(base * 0.05);
        const userGst = Math.round(userFee * 0.18);
        const partnerFee = Math.round(base * 0.05);
        const partnerGst = Math.round(partnerFee * 0.18);
        setFeeBreakdown({
          base_amount: base,
          user_platform_fee: userFee,
          user_gst: userGst,
          total_payable: base + userFee + userGst,
          partner_platform_fee: partnerFee,
          partner_gst: partnerGst,
          partner_payout: base - partnerFee - partnerGst,
          admin_earnings: userFee + userGst + partnerFee + partnerGst,
          user_platform_fee_percent: 5,
          partner_platform_fee_percent: 5,
          gst_rate: 18,
        });
      });
  }, [discountedBase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derived totals from breakdown (or 0 before slots are selected)
  const totalAmount      = feeBreakdown?.total_payable     ?? discountedBase;
  const userPlatformFee  = feeBreakdown?.user_platform_fee ?? 0;
  const userGst          = feeBreakdown?.user_gst          ?? 0;
  const feePercent       = feeBreakdown?.user_platform_fee_percent ?? 5;

  useEffect(() => {
    const isTest = process.env.REACT_APP_PAYU_IS_TEST === 'true';
    const scriptSrc = isTest 
      ? 'https://jssdk-uat.payu.in/bolt/bolt.min.js' 
      : 'https://jssdk.payu.in/bolt/bolt.min.js';

    const script = document.createElement('script');
    script.src = scriptSrc;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    if (!turfId || !date) return;

    setSlotsLoading(true);
    axios.get(`${API}/bookings/booked-slots/${turfId}/${date}`)
      .then(res => {
        const slots = res.data.bookedSlots || [];
        setBookedSlots(slots);
      })
      .catch(err => {
        console.error(err);
        setBookedSlots([]);
      })
      .finally(() => setSlotsLoading(false));
  }, [turfId, date]);

  // ── PAST SLOT DETECTION ──────────────────────────────────────────────────
  // Returns true if this slot hour is in the past (only relevant for today)
  const isSlotInPast = (slotHour) => {
    const today = new Date().toISOString().split('T')[0];
    if (date !== today) return false;           // future date → nothing is past
    const nowHour = new Date().getHours();      // e.g. 14 for 2 PM
    // Slot "14-15" starts at hour 14; disable it once the clock passes 14:00
    return slotHour <= nowHour;
  };

  const isSlotBooked = (slotLabel) => {
    return bookedSlots.includes(slotLabel);
  };

  const toggleSlotSelection = (slotLabel) => {
    if (selectedSlots.includes(slotLabel)) {
      setSelectedSlots(selectedSlots.filter(s => s !== slotLabel));
    } else {
      setSelectedSlots([...selectedSlots, slotLabel]);
    }
  };

  const [paymentMethod, setPaymentMethod] = useState('razorpay'); // razorpay | upi | card | wallet

  const handleSendRequest = async () => {
    if (!token) return navigate('/login');
    if (selectedSlots.length === 0) return alert('Please select at least one time slot');
    
    setLoading(true);
    try {
      // If total amount is 0, skip payment and book directly
      if (totalAmount === 0) {
        await axios.post(`${API}/bookings/direct`, {
          turf_id: turfId,
          date,
          time_slots: selectedSlots,
          total_price: 0,
          payment_id: "free_booking_" + Date.now(),
        }, { headers: { Authorization: `Bearer ${token}` } });
        setRequestSent(true);
        setLoading(false);
        return;
      }

      // Create PayU order / hash parameters
      const orderRes = await axios.post(`${API}/bookings/payu-order`, {
        amount: totalAmount,
        turf_id: turfId,
        date,
        time_slots: selectedSlots,
        sport: turf?.sport || ''
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      const { key, txnid, amount, firstname, email, phone, productinfo, hash, udf1, udf2, udf3, udf4, udf5, demo, payu_url } = orderRes.data;

      if (demo) {
        // Bypass PayU for demo - direct booking
        await axios.post(`${API}/bookings/direct`, {
          turf_id: turfId,
          date,
          time_slots: selectedSlots,
          total_price: totalAmount,
          payment_id: "demo_payu_" + Date.now(),
        }, { headers: { Authorization: `Bearer ${token}` } });
        
        setRequestSent(true);
        setLoading(false);
        return;
      }

      // Redirect to PayU payment page
      const targetPayuUrl = payu_url || (process.env.REACT_APP_PAYU_IS_TEST === 'true'
        ? 'https://test.payu.in/_payment'
        : 'https://secure.payu.in/_payment');

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = targetPayuUrl;

      const cbUrl = `${API}/bookings/payu-callback`;
      const secureCbUrl = cbUrl.startsWith('http://') && !API.includes('localhost') && !API.includes('127.0.0.1')
        ? 'https://' + cbUrl.slice(7)
        : cbUrl;

      const params = {
        key,
        txnid,
        amount: parseFloat(amount).toFixed(2),
        productinfo,
        firstname,
        email,
        phone: phone ? phone.replace(/\D/g, '') : '',
        surl: secureCbUrl,
        furl: secureCbUrl,
        hash,
        udf1: udf1 || '',
        udf2: udf2 || '',
        udf3: udf3 || '',
        udf4: udf4 || '',
        udf5: udf5 || ''
      };

      Object.entries(params).forEach(([k, v]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = k;
        input.value = v || '';
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
      return;
    } catch (err) {
      console.error(err);
      const errorMsg = err.response?.data?.msg || err.message;
      alert(`Failed to create booking: ${errorMsg}`);
    }
    setLoading(false);
  };

  if (turfLoading) return (
    <div style={{ textAlign:'center', padding:'6rem 2rem', minHeight:'calc(100vh - 72px)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
      <div style={{ width:'48px', height:'48px', border:'4px solid #EEF2E6', borderTop:'4px solid #084734', borderRadius:'50%', animation:'spin 1s linear infinite', margin:'0 auto 1rem' }} />
      <p style={{ color:'#98A2B3', fontSize:'1.1rem', fontWeight:'600' }}>Loading venue details...</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (turfError || !turf) return (
    <div style={{ textAlign:'center', padding:'6rem 2rem', minHeight:'calc(100vh - 72px)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
      <div style={{ fontSize:'4rem', marginBottom:'1rem' }}>🏟️</div>
      <h2 style={{ color:'#161616', marginBottom:'1rem', fontSize: '2rem', fontWeight: '900' }}>Venue Not Found</h2>
      <p style={{ color:'#666', marginBottom:'2rem', fontWeight: '500' }}>The venue you're looking for doesn't exist or the link is invalid.</p>
      <button onClick={() => navigate('/explore')} style={{ background:'#084734', color:'#8ECA25', border:'none', padding:'14px 40px', borderRadius:'14px', cursor:'pointer', fontWeight:'800', fontSize: '1rem' }}>Browse Venues</button>
    </div>
  );

  if (requestSent) return (
    <div style={{ textAlign:'center', padding:'4rem 2rem', maxWidth:'500px', margin:'0 auto', minHeight:'calc(100vh - 72px)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
      <div style={{ width:'80px', height:'80px', borderRadius:'50%', background:'#D0F08B', border: '2px solid #8ECA25', display:'flex', alignItems:'center', justifyContent:'center', color:'#8ECA25', fontSize:'2.5rem', marginBottom:'1.5rem', fontWeight: '900' }}>✓</div>
      <h2 style={{ color:'#161616', marginBottom:'0.75rem', fontSize:'2.2rem', fontWeight: '900' }}>Booking Confirmed!</h2>
      <p style={{ color:'#161616', fontWeight: '700', fontSize: '1.1rem', marginBottom:'0.5rem' }}>{turf.name}</p>
      <p style={{ color:'#666', marginBottom:'1.5rem', fontWeight: '500' }}>{date} | {selectedSlots.join(', ')}</p>
      <div style={{ background:'#f8f9fa', borderRadius:'20px', padding:'1.5rem 2.5rem', marginTop:'1rem', marginBottom:'2.5rem', border:'1.5px solid #eee' }}>
        <div style={{ fontSize:'0.9rem', color:'#888', fontWeight: '600', marginBottom: '4px' }}>Total Amount Paid</div>
        <div style={{ fontSize:'1.8rem', fontWeight:'900', color:'#8ECA25' }}>INR {totalAmount.toLocaleString()}</div>
      </div>
      <button onClick={() => navigate('/my-bookings')} style={{ background:'#8ECA25', color:'#084734', border:'none', padding:'16px 48px', borderRadius:'16px', cursor:'pointer', fontWeight:'800', fontSize: '1.05rem', boxShadow: '0 8px 25px rgba(8,71,52,0.2)' }}>View My Bookings</button>
    </div>
  );

  const inputStyle = { padding:'12px 16px', borderRadius:'12px', border:'1.5px solid #eee', fontSize:'0.95rem', fontWeight:'700', cursor:'pointer', minWidth:'180px', outline:'none', background: 'white' };
  const rowStyle = { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' };

  return (
    <div style={{ background:'#F8FAF7', minHeight:'calc(100vh - 72px)' }}>
      <div style={{ maxWidth:'1100px', margin:'0 auto', padding: isMobile ? '1.5rem 1rem 0' : '3rem 2rem 0' }}>
        <BackButton />
      </div>
      <div style={{ maxWidth:'1100px', margin:'0 auto', padding: isMobile ? '1rem 1rem 1.5rem' : '1.5rem 2rem 3rem', display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 400px', gap: isMobile ? '1.5rem' : '3rem', alignItems:'start' }}>
        {/* LEFT */}
        <div>
          <div style={{ background:'white', borderRadius:'24px', padding:'2rem', border:'1px solid #eee', marginBottom:'2rem', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div>
                <h2 style={{ fontSize:'1.8rem', fontWeight:'900', color:'#161616', marginBottom:'8px', letterSpacing: '-0.5px' }}>{turf.name}</h2>
                <p style={{ color:'#98A2B3', fontSize:'1rem', fontWeight: '500' }}>{turf.location}, {turf.city}</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#f8f9fa', padding: '6px 14px', borderRadius: '12px', border: '1px solid #eee' }}>
                 <span style={{ fontWeight: '800', fontSize: '1rem', color: '#161616' }}>{turf.rating} Rating</span>
              </div>
            </div>
          </div>

          <div style={{ background:'white', borderRadius:'24px', padding:'2rem', border:'1px solid #eee', marginBottom:'2rem', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
            <div style={rowStyle}>
              <label style={{ fontWeight:'800', color:'#161616', fontSize: '1rem' }}>Activity</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {SPORTS.map(s => (
                  <button 
                    key={s}
                    onClick={() => setSport(s)}
                    style={{
                      padding: '10px 18px', borderRadius: '12px',
                      border: `1.5px solid ${sport === s ? '#8ECA25' : '#eee'}`,
                      background: sport === s ? '#8ECA25' : 'white',
                      color: sport === s ? '#084734' : '#374151',
                      fontWeight: '800', fontSize: '0.9rem', cursor: 'pointer',
                      transition: '0.2s all', outline: 'none'
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div style={rowStyle}>
              <label style={{ fontWeight:'800', color:'#161616', fontSize: '1rem' }}>Date</label>
              <input type="date" value={date} onChange={e => {
                const newDate = e.target.value;
                setDate(newDate);
                // Clear slots that are now in the past when switching to today
                const today = new Date().toISOString().split('T')[0];
                if (newDate === today) {
                  const nowHour = new Date().getHours();
                  setSelectedSlots(prev =>
                    prev.filter(label => {
                      const slot = hourlySlots.find(s => s.label === label);
                      return slot ? slot.hour > nowHour : true;
                    })
                  );
                } else {
                  setSelectedSlots([]);
                }
              }} min={new Date().toISOString().split('T')[0]} style={inputStyle} />
            </div>
            <div style={rowStyle}>
              <label style={{ fontWeight:'800', color:'#161616', fontSize: '1rem' }}>Select Time Slot</label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '1.5rem' }}>
              {slotsLoading ? (
                <div style={{ gridColumn: '1 / -1', color: '#94a3b8', fontWeight: '700', textAlign: 'center', padding: '1rem' }}>Loading slots...</div>
              ) : hourlySlots.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', background: '#F8FAF7', padding: '1.5rem', borderRadius: '16px', border: '1.5px dashed #CBD5E1', textAlign: 'center', color: '#475569', fontWeight: '700', fontSize: '0.9rem' }}>
                  No slots available for this date.
                </div>
              ) : (
                hourlySlots.map((slot) => {
                  const isBooked = isSlotBooked(slot.label);
                  const isPast   = isSlotInPast(slot.hour);
                  const isUnavailable = isBooked || isPast;
                  const isSelected = selectedSlots.includes(slot.label);
                  return (
                    <button
                      key={slot.value}
                      disabled={isUnavailable}
                      onClick={() => {
                        if (!isUnavailable) toggleSlotSelection(slot.label);
                      }}
                      style={{
                        padding: '12px 8px',
                        borderRadius: '12px',
                        border: `2px solid ${isSelected ? '#084734' : isBooked ? '#FCA5A5' : isPast ? '#CBD5E1' : '#E2E8F0'}`,
                        background: isSelected ? '#CEF17B' : isBooked ? '#FEE2E2' : isPast ? '#E2E8F0' : 'white',
                        color: isSelected ? '#084734' : isBooked ? '#991B1B' : isPast ? '#334155' : '#0F172A',
                        cursor: isUnavailable ? 'not-allowed' : 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '800',
                        transition: 'all 0.2s',
                        textAlign: 'center',
                        position: 'relative',
                        zIndex: 1,
                        outline: 'none',
                        userSelect: 'none',
                        opacity: 1,
                        boxShadow: isSelected ? '0 4px 12px rgba(8,71,52,0.15)' : 'none'
                      }}
                    >
                      {isSelected && <span style={{ position: 'absolute', top: '4px', right: '6px', fontSize: '0.9rem', fontWeight: '900' }}>✓</span>}
                      <div style={{ color: isSelected ? '#084734' : isBooked ? '#991B1B' : isPast ? '#334155' : '#0F172A', fontWeight: '800' }}>
                        {slot.label}
                      </div>
                      {isBooked && (
                        <div style={{ fontSize: '0.72rem', marginTop: '4px', fontWeight: '800', color: '#B91C1C' }}>
                          🔒 Booked
                        </div>
                      )}
                      {isPast && !isBooked && (
                        <div style={{ fontSize: '0.72rem', marginTop: '4px', fontWeight: '800', color: '#475569' }}>
                          ⏳ Past
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
            {selectedSlots.length > 0 && (
              <div style={{ background: '#D0F08B', border: '1.5px solid #D0F08B', borderRadius: '12px', padding: '12px 16px', marginBottom: '1.5rem' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#084734', marginBottom: '6px' }}>
                  Selected Slots ({selectedSlots.length})
                </div>
                <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#15803d' }}>
                  {selectedSlots.join(', ')}
                </div>
              </div>
            )}
          </div>

          <div style={{ background:'white', borderRadius:'24px', padding:'2rem', border:'1px solid #eee' }}>
            <h3 style={{ fontSize:'1.1rem', fontWeight:'800', marginBottom:'1rem', color: '#161616' }}>Policies</h3>
            <p style={{ fontSize:'0.9rem', color:'#666', lineHeight:1.7, marginBottom:'1.5rem', fontWeight: '500' }}>Cancellations allowed up to 2 hours before the slot. A 15% fee applies for late cancellations.</p>
            <h3 style={{ fontSize:'1.1rem', fontWeight:'800', marginBottom:'1rem', color: '#161616' }}>Venue Rules</h3>
            <p style={{ fontSize:'0.9rem', color:'#666', lineHeight:1.7, margin:0, fontWeight: '500', whiteSpace: 'pre-line' }}>
              {turf?.description || 'No special rules specified for this venue.'}
            </p>
          </div>
        </div>

        {/* RIGHT */}
        <div style={{ position: isMobile ? 'static' : 'sticky', top:'100px' }}>
          <div style={{ background:'white', borderRadius:'24px', padding:'2rem', border:'1px solid #eee', marginBottom:'1.5rem', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
            <h3 style={{ fontSize:'1.2rem', fontWeight:'900', color: '#161616', marginBottom: '1.5rem' }}>Booking Summary</h3>
            <div style={{ background:'#F8FAF7', borderRadius:'16px', padding:'1.25rem', border:'1.5px solid #EEF2E6' }}>
              <div style={{ fontWeight:'800', fontSize:'1.05rem', marginBottom:'6px', color: '#161616' }}>{turf.name}</div>
              
              {/* Address */}
              <div style={{ fontSize:'0.85rem', color:'#666', fontWeight: '500', marginBottom:'12px', display:'flex', alignItems:'flex-start', gap:'4px' }}>
                <span style={{ fontSize:'0.95rem' }}>📍</span>
                <span>{turf.location}, {turf.city}{turf.state ? `, ${turf.state}` : ''}{turf.pincode ? ` - ${turf.pincode}` : ''}</span>
              </div>
              
              <div style={{ borderTop:'1px dashed #E2E8F0', margin:'10px 0' }} />
              
              {/* Timing */}
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'6px', fontSize:'0.85rem' }}>
                <span style={{ color:'#98A2B3', fontWeight:'600' }}>Date</span>
                <span style={{ color:'#161616', fontWeight:'700' }}>{date}</span>
              </div>
              
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'12px', fontSize:'0.85rem' }}>
                <span style={{ color:'#98A2B3', fontWeight:'600' }}>Selected Slots</span>
                <span style={{ color:'#8ECA25', fontWeight:'800', textAlign:'right' }}>
                  {selectedSlots.length > 0 ? selectedSlots.join(', ') : 'None'}
                </span>
              </div>

              <div style={{ borderTop:'1px dashed #E2E8F0', margin:'10px 0' }} />

              {/* People Details: User & Owner */}
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'6px', fontSize:'0.85rem' }}>
                <span style={{ color:'#98A2B3', fontWeight:'600' }}>Player (User)</span>
                <span style={{ color:'#161616', fontWeight:'700' }}>{user?.name || 'Guest'}</span>
              </div>
              
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'6px', fontSize:'0.85rem' }}>
                <span style={{ color:'#98A2B3', fontWeight:'600' }}>Venue Owner</span>
                <span style={{ color:'#161616', fontWeight:'700' }}>{turf.owner_id?.name || 'N/A'}</span>
              </div>

              <div style={{ borderTop:'1px dashed #E2E8F0', margin:'10px 0' }} />

              {/* Total Amount */}
              {discountAmount > 0 && (
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'4px', fontSize:'0.82rem', color:'#15803d' }}>
                  <span style={{ fontWeight:'600' }}>Offer Discount</span>
                  <span style={{ fontWeight:'700' }}>-₹{discountAmount.toLocaleString()}</span>
                </div>
              )}

              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'8px', fontSize:'0.9rem' }}>
                <span style={{ color:'#161616', fontWeight:'800' }}>Amount</span>
                <span style={{ color:'#084734', fontWeight:'900', fontSize:'1.1rem' }}>₹{totalAmount.toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div style={{ background:'white', borderRadius:'24px', padding:'2rem', border:'1px solid #eee', marginBottom:'1.5rem', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
            <h3 style={{ fontSize:'1.2rem', fontWeight:'900', marginBottom:'1.5rem', color: '#161616' }}>Price Breakdown</h3>

            {/* ── Base / per-slot rows ── */}
            {selectedSlots.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                {selectedSlots.map((label, i) => (
                  <div key={label} style={{ display:'flex', justifyContent:'space-between', alignItems: 'center', fontSize:'0.88rem', background: '#F8FAF7', padding: '8px 12px', borderRadius: '10px', border: '1px solid #EEF2E6' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '0.8rem' }}>🕒</span>
                      <span style={{ color:'#084734', fontWeight:'700' }}>{label}</span>
                    </div>
                    <span style={{ fontWeight:'850', color:'#161616' }}>₹{slotPrices[i].toLocaleString()}</span>
                  </div>
                ))}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems: 'center', marginTop: '4px', fontSize:'0.92rem', borderTop: '1px dashed #E2E8F0', paddingTop: '8px' }}>
                  <span style={{ color:'#64748B', fontWeight:'700' }}>Court Base Total ({numberOfSlots} {numberOfSlots === 1 ? 'Slot' : 'Slots'})</span>
                  <span style={{ fontWeight:'900', color:'#084734', fontSize: '1.05rem' }}>₹{courtPrice.toLocaleString()}</span>
                </div>
              </div>
            ) : (
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'6px', fontSize:'0.95rem' }}>
                <span style={{ color:'#98A2B3', fontWeight:'600' }}>Base Court Amount</span>
                <span style={{ fontWeight:'800', color:'#161616' }}>₹0</span>
              </div>
            )}

            {/* ── Offer Discount Row ── */}
            {discountAmount > 0 && (
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'6px', marginTop:'6px', fontSize:'0.95rem' }}>
                <span style={{ color:'#98A2B3', fontWeight:'600' }}>
                  Discount ({discountPercent}% OFF)
                </span>
                <span style={{ fontWeight:'800', color:'#16a34a' }}>
                  -₹{discountAmount.toLocaleString()}
                </span>
              </div>
            )}

            {/* ── Fee rows (always visible when slots selected) ── */}
            {numberOfSlots > 0 && feeBreakdown && (
              <div style={{ marginTop:'10px', background:'#F8FAF7', borderRadius:'14px', border:'1.5px solid #EEF2E6', overflow:'hidden' }}>
                <button 
                  onClick={() => setShowFeeBreakdown(!showFeeBreakdown)}
                  style={{ 
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 14px', 
                    border: 'none',
                    background: '#EEF2E6',
                    cursor: 'pointer',
                    outline: 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  <span style={{ fontSize:'0.75rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'1px' }}>
                    Fee Breakdown
                  </span>
                  <span style={{ fontSize: '0.8rem', color: '#374151', fontWeight: 'bold' }}>
                    {showFeeBreakdown ? '▲' : '▼'}
                  </span>
                </button>

                {showFeeBreakdown && (
                  <div style={{ borderTop: '1px solid #EEF2E6' }}>
                    {/* Platform fee */}
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'9px 14px', borderBottom:'1px solid #EEF2E6' }}>
                      <span style={{ fontSize:'0.85rem', color:'#374151', fontWeight:'600' }}>Platform Fee</span>
                      <span style={{ fontSize:'0.88rem', fontWeight:'800', color:'#161616' }}>₹{userPlatformFee.toLocaleString()}</span>
                    </div>

                    {/* GST on platform fee */}
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'9px 14px', borderBottom:'1px solid #EEF2E6' }}>
                      <div>
                        <span style={{ fontSize:'0.85rem', color:'#374151', fontWeight:'600' }}>GST</span>
                        <span style={{ fontSize:'0.7rem', color:'#98A2B3', marginLeft:'6px' }}>18% on platform fee</span>
                      </div>
                      <span style={{ fontSize:'0.88rem', fontWeight:'800', color:'#161616' }}>₹{userGst.toLocaleString()}</span>
                    </div>

                    {/* Total payable */}
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', background:'#D0F08B' }}>
                      <span style={{ fontSize:'0.88rem', fontWeight:'800', color:'#084734' }}>Total Payable</span>
                      <span style={{ fontSize:'1rem', fontWeight:'900', color:'#084734' }}>₹{totalAmount.toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Grand total ── */}
            <div style={{ borderTop:'1.5px solid #EEF2E6', marginTop:'14px', paddingTop:'14px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontWeight:'900', fontSize:'1.15rem', color:'#161616' }}>Total</span>
              <span style={{ fontWeight:'900', fontSize:'1.25rem', color:'#084734' }}>
                ₹{totalAmount.toLocaleString()}
              </span>
            </div>
          </div>

          <button onClick={handleSendRequest} disabled={loading || selectedSlots.length === 0} style={{
            width:'100%', background: loading || selectedSlots.length === 0 ? '#D0F08B' : '#8ECA25',
            color:'#084734', border:'none', padding:'20px', borderRadius:'20px',
            cursor: loading || selectedSlots.length === 0 ? 'not-allowed' : 'pointer', fontWeight:'900', fontSize:'1.15rem',
            boxShadow: selectedSlots.length > 0 ? '0 10px 30px rgba(8,71,52,0.2)' : 'none', transition:'all 0.3s',
          }}>{loading ? 'Processing...' : selectedSlots.length === 0 ? 'Select Slots' : `Pay INR ${totalAmount.toLocaleString()}`}</button>

          <div style={{ textAlign:'center', marginTop:'1rem', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
            <span style={{ fontSize:'0.75rem' }}>🔒</span>
            <p style={{ fontSize:'0.8rem', color:'#94a3b8', fontWeight:'700', margin:0 }}>Secured by Razorpay · 256-bit SSL</p>
          </div>
        </div>
      </div>
    </div>
  );
}
